import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { NavContentBlueprint } from '@backstage/plugin-app-react';
import { NotificationsSidebarItem } from '@backstage/plugin-notifications';
import { SearchModal, useSearchModal } from '@backstage/plugin-search';
import {
  UserSettingsSignInAvatar,
  useUserProfile,
} from '@backstage/plugin-user-settings';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import Drawer from '@material-ui/core/Drawer';
import IconButton from '@material-ui/core/IconButton';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import ListSubheader from '@material-ui/core/ListSubheader';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Tooltip from '@material-ui/core/Tooltip';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import AccountCircleOutlined from '@material-ui/icons/AccountCircleOutlined';
import AddBoxOutlined from '@material-ui/icons/AddBoxOutlined';
import AddCircleOutline from '@material-ui/icons/AddCircleOutline';
import CategoryOutlined from '@material-ui/icons/CategoryOutlined';
import DescriptionOutlined from '@material-ui/icons/DescriptionOutlined';
import DeviceHubOutlined from '@material-ui/icons/DeviceHubOutlined';
import ExitToAppOutlined from '@material-ui/icons/ExitToAppOutlined';
import ExtensionOutlined from '@material-ui/icons/ExtensionOutlined';
import FavoriteBorder from '@material-ui/icons/FavoriteBorder';
import HomeOutlined from '@material-ui/icons/HomeOutlined';
import ListAltOutlined from '@material-ui/icons/ListAltOutlined';
import MenuBookOutlined from '@material-ui/icons/MenuBookOutlined';
import PeopleOutline from '@material-ui/icons/PeopleOutline';
import ShareOutlined from '@material-ui/icons/ShareOutlined';
import VisibilityOutlined from '@material-ui/icons/VisibilityOutlined';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import { ReactNode, useEffect, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import logo from '../../assets/logo-vanti.png';
import logoBlanco from '../../assets/logo-vanti-blanco.png';

export const ALTO_MENU = 80;

/** Entrada ya resuelta del menú. */
type Entrada = { id: string; titulo: string; href: string; icono: ReactNode };
type Grupo = { titulo: string; entradas: Entrada[] };

const ROLES: Array<[string, string]> = [
  ['group:default/admin', 'Admin'],
  ['group:default/devops', 'DevOps'],
  ['group:default/lideres-tecnicos', 'Líder técnico'],
  ['group:default/desarrolladores', 'Desarrollador'],
];

const useStyles = makeStyles(theme => ({
  barra: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    height: ALTO_MENU,
    padding: theme.spacing(0, 3),
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down('xs')]: { padding: theme.spacing(0, 1) },
    '@media print': { display: 'none' },
  },
  marca: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    color: 'inherit',
    textDecoration: 'none',
  },
  logo: { height: 44, width: 'auto', display: 'block' },
  separador: {
    width: 1,
    height: 28,
    margin: theme.spacing(0, 2),
    backgroundColor: theme.palette.text.primary,
  },
  producto: { fontWeight: 700, fontSize: 16, whiteSpace: 'nowrap' },
  navegacion: {
    display: 'flex',
    alignItems: 'stretch',
    alignSelf: 'stretch',
    marginLeft: theme.spacing(3),
  },
  enlace: {
    borderRadius: 0,
    minHeight: 0,
    padding: theme.spacing(0, 1.5),
    fontSize: 14,
    fontWeight: 700,
    whiteSpace: 'nowrap',
    color: theme.palette.text.primary,
    borderBottom: '3px solid transparent',
    borderTop: '3px solid transparent',
    '&:hover': { backgroundColor: theme.palette.action.hover },
  },
  activo: { borderBottomColor: theme.palette.status.pending },
  espacio: { flex: 1 },
  // NotificationsSidebarItem trae el contador y los avisos, pero se pinta con
  // los colores de la barra lateral.
  notificaciones: {
    display: 'flex',
    '& a': {
      width: 48,
      minWidth: 48,
      height: 48,
      borderRadius: '50%',
      borderLeft: 'none !important',
      color: `${theme.palette.text.primary} !important`,
    },
    '& a:hover': { backgroundColor: theme.palette.action.hover },
    '& svg': { color: theme.palette.text.primary },
    '& a > *': { margin: 0 },
  },
  usuario: {
    marginLeft: theme.spacing(1),
    padding: theme.spacing(0.5, 1),
    minHeight: 0,
    color: theme.palette.text.primary,
    textAlign: 'right',
    '&:hover': { backgroundColor: theme.palette.action.hover },
  },
  datosUsuario: {
    display: 'flex',
    flexDirection: 'column',
    marginRight: theme.spacing(1),
    lineHeight: 1.25,
    maxWidth: 180,
  },
  rol: {
    fontSize: 12,
    fontWeight: 400,
    color: theme.palette.text.secondary,
  },
  nombre: {
    fontSize: 14,
    fontWeight: 700,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // Desplegables: ícono de contorno y texto navy en negrita
  menuPanel: { borderRadius: 12, minWidth: 260 },
  menuItem: {
    padding: theme.spacing(1.5, 2),
    fontSize: 16,
    fontWeight: 700,
    color: theme.palette.primary.main,
  },
  menuIcono: { minWidth: 40, color: theme.palette.primary.main },
  cajon: { width: 280 },
}));

const estaActiva = (pathname: string, href: string) =>
  href === '/' ? pathname === '/' : pathname === href || pathname.startsWith(`${href}/`);

/** Botón con su desplegable. */
const Desplegable = ({ grupo }: { grupo: Grupo }) => {
  const classes = useStyles();
  const { pathname } = useLocation();
  const [ancla, setAncla] = useState<HTMLElement | null>(null);
  const activo = grupo.entradas.some(e => estaActiva(pathname, e.href));
  return (
    <>
      <Button
        className={`${classes.enlace} ${activo ? classes.activo : ''}`}
        endIcon={<ExpandMoreIcon />}
        aria-haspopup="menu"
        aria-expanded={Boolean(ancla)}
        onClick={e => setAncla(e.currentTarget)}
      >
        {grupo.titulo}
      </Button>
      <Menu
        anchorEl={ancla}
        open={Boolean(ancla)}
        onClose={() => setAncla(null)}
        getContentAnchorEl={null}
        PaperProps={{ className: classes.menuPanel }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {grupo.entradas.map(e => (
          <MenuItem
            key={e.id}
            className={classes.menuItem}
            component={RouterLink}
            to={e.href}
            selected={estaActiva(pathname, e.href)}
            onClick={() => setAncla(null)}
          >
            <ListItemIcon className={classes.menuIcono}>{e.icono}</ListItemIcon>
            {e.titulo}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

/** Nombre y rol, con el desplegable de la cuenta. */
const MenuUsuario = ({ entradas, rol }: { entradas: Entrada[]; rol?: string }) => {
  const classes = useStyles();
  const identity = useApi(identityApiRef);
  const { displayName } = useUserProfile();
  const compacto = useMediaQuery('(max-width:600px)');
  const [ancla, setAncla] = useState<HTMLElement | null>(null);
  return (
    <>
      <Button
        className={classes.usuario}
        aria-label={`Cuenta de ${displayName}`}
        aria-haspopup="menu"
        onClick={e => setAncla(e.currentTarget)}
      >
        {!compacto && (
          <span className={classes.datosUsuario}>
            {rol && <span className={classes.rol}>{rol}</span>}
            <span className={classes.nombre}>{displayName}</span>
          </span>
        )}
        <UserSettingsSignInAvatar size={36} />
      </Button>
      <Menu
        anchorEl={ancla}
        open={Boolean(ancla)}
        onClose={() => setAncla(null)}
        getContentAnchorEl={null}
        PaperProps={{ className: classes.menuPanel }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {entradas.map(e => (
          <MenuItem key={e.id} className={classes.menuItem} component={RouterLink} to={e.href} onClick={() => setAncla(null)}>
            <ListItemIcon className={classes.menuIcono}>{e.icono}</ListItemIcon>
            {e.titulo}
          </MenuItem>
        ))}
        <Divider />
        <MenuItem className={classes.menuItem} onClick={() => identity.signOut()}>
          <ListItemIcon className={classes.menuIcono}>
            <ExitToAppOutlined />
          </ListItemIcon>
          Cerrar sesión
        </MenuItem>
      </Menu>
    </>
  );
};

/** En pantallas angostas, el mismo menú en un panel lateral. */
const MenuMovil = ({ directos, grupos }: { directos: Entrada[]; grupos: Grupo[] }) => {
  const classes = useStyles();
  const { pathname } = useLocation();
  const [abierto, setAbierto] = useState(false);
  const fila = (e: Entrada) => (
    <ListItem
      button
      key={e.id}
      className={classes.menuItem}
      component={RouterLink}
      to={e.href}
      selected={estaActiva(pathname, e.href)}
      onClick={() => setAbierto(false)}
    >
      <ListItemIcon className={classes.menuIcono}>{e.icono}</ListItemIcon>
      <ListItemText primary={e.titulo} primaryTypographyProps={{ variant: 'inherit' }} />
    </ListItem>
  );
  return (
    <>
      <IconButton aria-label="Abrir menú" onClick={() => setAbierto(true)}>
        <MenuIcon />
      </IconButton>
      <Drawer anchor="left" open={abierto} onClose={() => setAbierto(false)}>
        <List className={classes.cajon}>
          {directos.map(fila)}
          {grupos.map(g => (
            <li key={g.titulo}>
              <ul style={{ padding: 0 }}>
                <ListSubheader disableSticky>{g.titulo}</ListSubheader>
                {g.entradas.map(fila)}
              </ul>
            </li>
          ))}
        </List>
      </Drawer>
    </>
  );
};

const LogoVanti = () => {
  const classes = useStyles();
  const theme = useTheme();
  return (
    <img
      className={classes.logo}
      src={theme.palette.type === 'dark' ? logoBlanco : logo}
      alt="Vanti"
    />
  );
};

const Barra = ({ directos, grupos, cuenta }: {
  directos: { antes: Entrada[]; despues: Entrada[] };
  grupos: Grupo[];
  cuenta: Entrada[];
}) => {
  const classes = useStyles();
  const { pathname } = useLocation();
  const compacto = useMediaQuery('(max-width:1100px)');
  const sinNombre = useMediaQuery('(max-width:600px)');
  const identity = useApi(identityApiRef);
  const [grupoUsuario, setGrupoUsuario] = useState<string[]>([]);
  const { state, toggleModal } = useSearchModal();

  useEffect(() => {
    identity.getBackstageIdentity().then(i => setGrupoUsuario(i.ownershipEntityRefs));
  }, [identity]);

  const esAdmin = grupoUsuario.includes('group:default/admin');
  const rol = ROLES.find(([ref]) => grupoUsuario.includes(ref))?.[1];
  const entradasCuenta = cuenta.filter(e => e.id !== 'page:vanti-portal/usuarios' || esAdmin);
  const enlace = (e: Entrada) => (
    <Button
      key={e.id}
      component={RouterLink}
      to={e.href}
      className={`${classes.enlace} ${estaActiva(pathname, e.href) ? classes.activo : ''}`}
    >
      {e.titulo}
    </Button>
  );

  return (
    <header className={classes.barra}>
      {compacto && (
        <MenuMovil directos={[...directos.antes, ...directos.despues]} grupos={grupos} />
      )}
      <RouterLink to="/" className={classes.marca} aria-label="Inicio">
        <LogoVanti />
        {!sinNombre && (
          <>
            <span className={classes.separador} />
            <span className={classes.producto}>Vanti Arch</span>
          </>
        )}
      </RouterLink>
      {!compacto && (
        <nav className={classes.navegacion} aria-label="Principal">
          {directos.antes.map(enlace)}
          {grupos.map(g => (
            <Desplegable key={g.titulo} grupo={g} />
          ))}
          {directos.despues.map(enlace)}
        </nav>
      )}
      <span className={classes.espacio} />
      <Tooltip title="Buscar">
        <IconButton aria-label="Buscar" onClick={toggleModal}>
          <SearchIcon />
        </IconButton>
      </Tooltip>
      <SearchModal {...state} toggleModal={toggleModal} />
      <div className={classes.notificaciones}>
        <NotificationsSidebarItem />
      </div>
      <MenuUsuario entradas={entradasCuenta} rol={rol} />
    </header>
  );
};

/** Menú del portal: las páginas instaladas agrupadas; las nuevas caen en "Más". */
export const MenuSuperiorContent = NavContentBlueprint.make({
  params: {
    component: ({ navItems }) => {
      const nav = navItems.clone();
      const tomar = (id: string, icono: ReactNode, titulo?: string): Entrada[] => {
        const item = nav.take(id);
        return item
          ? [{ id, titulo: titulo ?? item.title, href: item.href, icono }]
          : [];
      };
      // Tienen su propio control en la barra
      nav.take('page:search');
      nav.take('page:notifications');

      const antes = tomar('page:home', <HomeOutlined />, 'Inicio');
      const grupos: Grupo[] = [
        {
          titulo: 'Catálogo',
          entradas: [
            ...tomar('page:catalog', <CategoryOutlined />, 'Catálogo completo'),
            ...tomar('page:vanti-portal/catalogo-servicios', <ListAltOutlined />),
            ...tomar('page:api-docs', <ExtensionOutlined />, 'APIs'),
            ...tomar('page:catalog-graph', <ShareOutlined />, 'Grafo del catálogo'),
            ...tomar('page:catalog-import', <AddCircleOutline />, 'Registrar existente'),
          ],
        },
        {
          titulo: 'Arquitectura',
          entradas: [
            ...tomar('page:vanti-portal/hoja-de-vida', <DescriptionOutlined />),
            ...tomar('page:vanti-portal/integraciones', <DeviceHubOutlined />),
            ...tomar('page:vanti-portal/salud', <FavoriteBorder />),
          ],
        },
      ];
      const despues = [
        ...tomar('page:scaffolder', <AddBoxOutlined />, 'Crear'),
        ...tomar('page:techdocs', <MenuBookOutlined />, 'Docs'),
      ];
      const cuenta = [
        ...tomar('page:user-settings', <AccountCircleOutlined />, 'Mi perfil'),
        ...tomar('page:vanti-portal/usuarios', <PeopleOutline />, 'Usuarios'),
        ...tomar('page:app-visualizer', <VisibilityOutlined />, 'Visualizador'),
      ];
      const mas = nav.rest().map(item => ({
        id: item.node.spec.id,
        titulo: item.title,
        href: item.href,
        icono: item.icon,
      }));
      if (mas.length) grupos.push({ titulo: 'Más', entradas: mas });

      return <Barra directos={{ antes, despues }} grupos={grupos} cuenta={cuenta} />;
    },
  },
});
